import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Edit3, Trash2, Save, StickyNote, Mail, Phone, MapPin, CalendarDays, IdCard, CreditCard, AlertTriangle, FileSignature, Send, FileText, Download, User } from "lucide-react";
import { Link } from "react-router-dom";
import {
  updateArtist,
  deleteArtist,
  updateCustomer,
  deleteCustomer,
  updateExternal,
  deleteExternal,
  fetchNotes,
  addNote,
  deleteNote,
  countArtistContracts,
  countCustomerContracts,
  countExternalContracts,
  fetchContractsForContact,
} from "../lib/driveClient";
import { supabase } from "../../lib/supabase";

/**
 * Detail-Modal für Artist / Customer / External mit:
 *  - View / Edit / Delete
 *  - HubSpot-style Notes Timeline
 *  - Verträge-Liste mit Download
 */
export default function ContactDetailModal({ contact, type, onClose, onChanged }) {
  const isArtist = type === "artist";
  const isCustomer = type === "customer";
  const isExternal = type === "external";
  const [mode, setMode] = useState("view");
  const [form, setForm] = useState(contact);
  const [notes, setNotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [linkedContracts, setLinkedContracts] = useState(0);
  const [showDelete, setShowDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const scopeArg = isArtist
    ? { artist_id: contact.id }
    : isCustomer
      ? { customer_id: contact.id }
      : { external_id: contact.id };

  useEffect(() => {
    setForm(contact);
    (async () => {
      try {
        const cntFn = isArtist ? countArtistContracts : isCustomer ? countCustomerContracts : countExternalContracts;
        const [n, c, cts] = await Promise.all([
          fetchNotes(scopeArg),
          cntFn(contact.id),
          fetchContractsForContact(scopeArg),
        ]);
        setNotes(n);
        setLinkedContracts(c);
        setContracts(cts);
      } catch (e) { toast.error(e.message); }
    })();
  }, [contact, isArtist, isCustomer, isExternal]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.id;
      const fn = isArtist ? updateArtist : isCustomer ? updateCustomer : updateExternal;
      const updated = await fn(contact.id, payload);
      toast.success("Aktualisiert");
      setForm(updated);
      setMode("view");
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const doDelete = async (withFiles) => {
    setBusy(true);
    try {
      const fn = isArtist ? deleteArtist : isCustomer ? deleteCustomer : deleteExternal;
      await fn(contact.id, { withFiles });
      toast.success(withFiles ? "Kontakt und Verträge gelöscht" : "Kontakt gelöscht — Verträge behalten");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); setShowDelete(false); }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setBusy(true);
    try {
      const note = await addNote({
        artist_id: isArtist ? contact.id : null,
        customer_id: isCustomer ? contact.id : null,
        external_id: isExternal ? contact.id : null,
        body: newNote.trim(),
      });
      setNotes((prev) => [note, ...prev]);
      setNewNote("");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const removeNote = async (id) => {
    try {
      await deleteNote(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (e) { toast.error(e.message); }
  };

  const downloadContract = async (contract) => {
    try {
      // Bevorzuge signierte Version wenn verfügbar
      const path = contract.signed_storage_path || contract.storage_path;
      const suffix = path.endsWith("-SIGNED.pdf") ? "-SIGNED" : "";
      const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(path, 60, { download: contract.title + suffix + ".pdf" });
      if (error) throw error;
      const a = document.createElement("a");
      a.href = data.signedUrl;
      a.download = contract.title + suffix + ".pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error(e.message); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const displayName = isArtist ? contact.artist_name : isCustomer ? contact.name : contact.full_name;
  const typeLabel = isArtist ? "Künstler" : isCustomer ? "Kunde" : "Extern";

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/[0.06] px-6 py-4 flex items-center justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">
              {typeLabel}{isExternal && contact.relation ? ` · ${contact.relation}` : ""}
            </div>
            <div className="font-display font-black uppercase text-xl md:text-2xl truncate">{displayName}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {mode === "view" ? (
              <>
                {(isArtist || isExternal) && (
                  <Link
                    to="/drive/contracts?action=new"
                    data-testid="detail-new-contract"
                    className="hidden md:inline-flex items-center gap-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white px-3 py-2 text-[10px] font-bold tracking-[0.25em] uppercase"
                  >
                    <FileSignature size={12} /> Vertrag
                  </Link>
                )}
                <button onClick={() => setMode("edit")} data-testid="detail-edit" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-zinc-300 hover:text-white px-3 py-2 text-[10px] font-bold tracking-[0.25em] uppercase">
                  <Edit3 size={12} /> Bearbeiten
                </button>
                <button onClick={() => setShowDelete(true)} data-testid="detail-delete" className="inline-flex items-center gap-2 border border-[#ff0033]/40 hover:border-[#ff0033] text-[#ff0033] hover:bg-[#ff0033]/10 px-3 py-2 text-[10px] font-bold tracking-[0.25em] uppercase">
                  <Trash2 size={12} /> Löschen
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { setForm(contact); setMode("view"); }} className="text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-white px-3">Abbrechen</button>
                <button onClick={save} disabled={busy} data-testid="detail-save" className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#ff0033] hover:text-white px-4 py-2 text-[10px] font-bold tracking-[0.25em] uppercase disabled:opacity-50">
                  <Save size={12} /> Speichern
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white"><X size={18} /></button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 grid md:grid-cols-[1fr_320px] gap-8">
          {/* Left: Fields */}
          <div className="space-y-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Stammdaten</div>
            {mode === "view" ? (
              <div className="space-y-3">
                {isArtist && (
                  <>
                    <ViewRow icon={IdCard} label="Vor- & Nachname" value={[contact.first_name, contact.last_name].filter(Boolean).join(" ")} />
                    <ViewRow icon={Mail} label="E-Mail" value={contact.email} />
                    <ViewRow icon={Phone} label="Telefon" value={contact.phone} />
                    <ViewRow icon={MapPin} label="Adresse" value={contact.address} />
                    <ViewRow icon={CalendarDays} label="Geburtsdatum" value={contact.birthdate ? new Date(contact.birthdate).toLocaleDateString("de-DE") : ""} />
                    <ViewRow icon={IdCard} label="Steuer-ID" value={contact.tax_id} />
                    <ViewRow icon={CreditCard} label="IBAN" value={contact.iban} />
                    {contact.notes && <ViewRow icon={StickyNote} label="Interne Notiz" value={contact.notes} multiline />}
                  </>
                )}
                {isCustomer && (
                  <>
                    <ViewRow icon={IdCard} label="Firma" value={contact.company} />
                    <ViewRow icon={Mail} label="E-Mail" value={contact.email} />
                    <ViewRow icon={Phone} label="Telefon" value={contact.phone} />
                    <ViewRow icon={MapPin} label="Adresse" value={contact.address} />
                    {contact.notes && <ViewRow icon={StickyNote} label="Interne Notiz" value={contact.notes} multiline />}
                  </>
                )}
                {isExternal && (
                  <>
                    <ViewRow icon={IdCard} label="Vor- & Nachname" value={[contact.first_name, contact.last_name].filter(Boolean).join(" ")} />
                    <ViewRow icon={User} label="Beziehung / Rolle" value={contact.relation} />
                    <ViewRow icon={Mail} label="E-Mail" value={contact.email} />
                    <ViewRow icon={Phone} label="Telefon" value={contact.phone} />
                    <ViewRow icon={MapPin} label="Adresse" value={contact.address} />
                    <ViewRow icon={CalendarDays} label="Geburtsdatum" value={contact.birthdate ? new Date(contact.birthdate).toLocaleDateString("de-DE") : ""} />
                    <ViewRow icon={IdCard} label="Steuer-ID" value={contact.tax_id} />
                    {contact.notes && <ViewRow icon={StickyNote} label="Interne Notiz" value={contact.notes} multiline />}
                  </>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {isArtist && <>
                  <EditField label="Vorname"><input value={form.first_name || ""} onChange={set("first_name")} className="edit-input" /></EditField>
                  <EditField label="Nachname"><input value={form.last_name || ""} onChange={set("last_name")} className="edit-input" /></EditField>
                  <EditField label="Künstlername *" full><input required value={form.artist_name || ""} onChange={set("artist_name")} className="edit-input" /></EditField>
                </>}
                {isCustomer && <>
                  <EditField label="Name *" full><input required value={form.name || ""} onChange={set("name")} className="edit-input" /></EditField>
                  <EditField label="Firma"><input value={form.company || ""} onChange={set("company")} className="edit-input" /></EditField>
                </>}
                {isExternal && <>
                  <EditField label="Vorname"><input value={form.first_name || ""} onChange={set("first_name")} className="edit-input" /></EditField>
                  <EditField label="Nachname"><input value={form.last_name || ""} onChange={set("last_name")} className="edit-input" /></EditField>
                  <EditField label="Anzeigename *" full><input required value={form.full_name || ""} onChange={set("full_name")} className="edit-input" /></EditField>
                  <EditField label="Beziehung / Rolle" full><input value={form.relation || ""} onChange={set("relation")} className="edit-input" placeholder="z.B. Bruder, Anwalt" /></EditField>
                </>}
                <EditField label="E-Mail"><input type="email" value={form.email || ""} onChange={set("email")} className="edit-input" /></EditField>
                <EditField label="Telefon"><input value={form.phone || ""} onChange={set("phone")} className="edit-input" /></EditField>
                <EditField label="Adresse" full><input value={form.address || ""} onChange={set("address")} className="edit-input" /></EditField>
                {(isArtist || isExternal) && <>
                  <EditField label="Geburtsdatum"><input type="date" value={form.birthdate || ""} onChange={set("birthdate")} className="edit-input" /></EditField>
                  <EditField label="Steuer-ID"><input value={form.tax_id || ""} onChange={set("tax_id")} className="edit-input" /></EditField>
                </>}
                {isArtist && (
                  <EditField label="IBAN" full><input value={form.iban || ""} onChange={set("iban")} className="edit-input" /></EditField>
                )}
                <EditField label="Interne Notiz (Stammdaten)" full>
                  <textarea rows={3} value={form.notes || ""} onChange={set("notes")} className="edit-input resize-none" />
                </EditField>
              </div>
            )}
          </div>

          {/* Right: Notes + Contracts */}
          <div className="space-y-6">
            {/* Contracts */}
            <div>
              <div className="flex items-center gap-2 mb-3 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                <FileText size={12} className="text-[#ff0033]" /> Verträge ({contracts.length})
              </div>
              <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                {contracts.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic">Noch keine Verträge.</div>
                ) : (
                  contracts.map((c) => (
                    <div key={c.id} data-testid={`ctc-contract-${c.id}`} className="group bg-[#0F0F0F] border border-white/[0.06] p-3 flex items-center justify-between gap-3 hover:border-[#ff0033]/30 transition-colors">
                      <div className="min-w-0">
                        <div className="text-xs text-white truncate">{c.title}</div>
                        <div className="text-[9px] tracking-[0.15em] uppercase text-zinc-600 mt-0.5">
                          {new Date(c.created_at).toLocaleDateString("de-DE")} · {c.status}
                        </div>
                      </div>
                      <button
                        onClick={() => downloadContract(c)}
                        title="Download"
                        className="p-2 text-zinc-400 hover:text-white shrink-0"
                      >
                        <Download size={13} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <div className="flex items-center gap-2 mb-3 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                <StickyNote size={12} className="text-[#ff0033]" /> Notizen ({notes.length})
              </div>
            <form onSubmit={submitNote} className="mb-4">
              <textarea
                data-testid="new-note"
                rows={3}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Neue Notiz hinzufügen…"
                className="w-full bg-transparent border border-white/10 focus:border-[#ff0033]/40 text-white text-sm p-3 outline-none resize-none"
              />
              <button
                type="submit"
                disabled={busy || !newNote.trim()}
                data-testid="submit-note"
                className="mt-2 inline-flex items-center gap-2 bg-white text-black hover:bg-[#ff0033] hover:text-white px-3 py-2 text-[10px] font-bold tracking-[0.25em] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send size={11} /> Hinzufügen
              </button>
            </form>

            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {notes.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">Noch keine Notizen.</div>
              ) : (
                notes.map((n) => (
                  <div key={n.id} className="group bg-[#0F0F0F] border-l-2 border-[#ff0033]/40 p-3 relative">
                    <button
                      onClick={() => removeNote(n.id)}
                      title="Notiz löschen"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[#ff0033]"
                    >
                      <X size={12} />
                    </button>
                    <div className="text-sm text-zinc-200 whitespace-pre-wrap pr-6">{n.body}</div>
                    <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 mt-2">
                      {new Date(n.created_at).toLocaleString("de-DE")}
                    </div>
                  </div>
                ))
              )}
            </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Overlay */}
        {showDelete && (
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-6">
            <div className="max-w-md w-full bg-[#0F0F0F] border border-[#ff0033]/40 p-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle size={28} className="text-[#ff0033] shrink-0" />
                <div>
                  <div className="font-display font-black uppercase text-lg leading-tight">{displayName} löschen?</div>
                  <div className="text-xs text-zinc-500 mt-1">Diese Aktion kann nicht rückgängig gemacht werden.</div>
                </div>
              </div>
              {linkedContracts > 0 && (
                <div className="text-sm text-zinc-300 bg-[#ff0033]/10 border border-[#ff0033]/30 p-3 mb-4">
                  Es sind <strong className="text-[#ff0033]">{linkedContracts}</strong> Vertrag/Verträge mit diesem Kontakt verknüpft.<br />
                  Wähle unten, was mit diesen Dateien passieren soll:
                </div>
              )}
              <div className="space-y-2">
                {linkedContracts > 0 && (
                  <button
                    onClick={() => doDelete(true)}
                    disabled={busy}
                    data-testid="delete-with-files"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#ff0033] text-white hover:brightness-110 px-4 py-3 text-[10px] font-bold tracking-[0.25em] uppercase disabled:opacity-50"
                  >
                    <Trash2 size={12} /> Löschen inkl. Verträge
                  </button>
                )}
                <button
                  onClick={() => doDelete(false)}
                  disabled={busy}
                  data-testid="delete-keep-files"
                  className="w-full inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white hover:bg-white/[0.03] px-4 py-3 text-[10px] font-bold tracking-[0.25em] uppercase disabled:opacity-50"
                >
                  {linkedContracts > 0 ? "Nur Kontakt löschen — Verträge behalten" : "Kontakt löschen"}
                </button>
                <button onClick={() => setShowDelete(false)} disabled={busy} className="w-full text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-white py-2">
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .edit-input { width:100%; background:transparent; border:1px solid rgba(255,255,255,0.1); padding:10px 12px; color:#fff; font-size:14px; outline:none; }
          .edit-input:focus { border-color: rgba(255,0,51,0.5); }
        `}</style>
      </div>
    </div>
  );
}

const ViewRow = ({ icon: Icon, label, value, multiline }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-white/[0.04]">
      <Icon size={13} className="text-[#ff0033] mt-1 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{label}</div>
        <div className={`text-sm text-white mt-0.5 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{value}</div>
      </div>
    </div>
  );
};

const EditField = ({ label, full, children }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">{label}</span>
    {children}
  </label>
);
